import { useState } from 'react';
import { Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  Locale,
  useAuthUserRestaurant,
  useGetRestaurantNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from '@durma-soft/gros-sdk';

import { Link } from '@/components/shared/link';
import { Spinner } from '@/components/shared/spinner';
import { Button } from '@/components/shared/shadcn-ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/shadcn-ui/popover';

import { cn } from '@/utils/helpers';
import { locales } from '@/config/global/languages';

export const NotificationsBell = () => {
  const { t, i18n } = useTranslation();
  const locale = locales[i18n.language as Locale];
  const [open, setOpen] = useState(false);

  const restaurant = useAuthUserRestaurant();
  const notifications = useGetRestaurantNotificationsQuery({
    restaurant_id: restaurant.id,
  });
  const markRead = useMarkNotificationReadMutation();
  const markAllRead = useMarkAllNotificationsReadMutation();

  const items = notifications.data ?? [];
  const hasUnread = items.some((notification) => notification.read_at === null);

  const markAsRead = (notificationId: number) => {
    markRead.mutate({
      restaurant_id: restaurant.id,
      notification_id: notificationId,
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative shrink-0">
          <Bell className="h-5 w-5" />
          {hasUnread && (
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-destructive" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
          <span className="font-semibold">{t('notifications.title')}</span>
          {hasUnread && (
            <Button
              variant="outline"
              size="sm"
              disabled={markAllRead.isPending}
              onClick={() =>
                markAllRead.mutate({ restaurant_id: restaurant.id })
              }
            >
              {t('notifications.mark-all-read')}
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.isLoading ? (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          ) : notifications.isError ? (
            <p className="p-4 text-center text-sm text-destructive">
              {t('common.error')}
            </p>
          ) : items.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              {t('notifications.empty')}
            </p>
          ) : (
            items.map((notification) => {
              const isUnread = notification.read_at === null;
              const className = cn(
                'flex w-full flex-col gap-1 border-b px-4 py-3 text-left last:border-b-0 hover:bg-accent',
                isUnread && 'bg-muted/50',
              );
              const content = (
                <>
                  <span className="flex min-w-0 items-start gap-2 text-sm">
                    {isUnread && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-destructive" />
                    )}
                    {/* Messages are authored only by us via the artisan command, so raw HTML is trusted. */}
                    <span
                      className="flex-1"
                      dangerouslySetInnerHTML={{ __html: notification.message }}
                    />
                  </span>
                  <span className="self-end text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(notification.created_at), {
                      addSuffix: true,
                      locale,
                    })}
                  </span>
                </>
              );

              if (!notification.url) {
                return (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => isUnread && markAsRead(notification.id)}
                    className={className}
                  >
                    {content}
                  </button>
                );
              }

              const onLinkClick = () => {
                if (isUnread) markAsRead(notification.id);
                setOpen(false);
              };

              if (/^https?:\/\//i.test(notification.url)) {
                return (
                  <a
                    key={notification.id}
                    href={notification.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onLinkClick}
                    className={className}
                  >
                    {content}
                  </a>
                );
              }

              return (
                <Link
                  key={notification.id}
                  to={notification.url}
                  onClick={onLinkClick}
                  className={className}
                >
                  {content}
                </Link>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
